import { Renderer } from '@client/core/Renderer';
import { PassiveTreeManager, PassiveNode, PassiveNodeType, CATEGORY_ICONS, NodeCategory } from '@shared/passives/PassiveTree';
import { Vector2 } from '@shared/types';
import customNodePositions from '@shared/passives/nodePositions.json';
import { debugLog } from '@shared/utils/Logger';
import { t } from '@locales/index';

// Category colors for node backgrounds
const CATEGORY_COLORS: Record<NodeCategory, { base: string; allocated: string }> = {
  strength: { base: '#3a2a1a', allocated: '#ff6644' },
  agility: { base: '#1a3a2a', allocated: '#44ff88' },
  spirit: { base: '#2a1a3a', allocated: '#aa44ff' },
  life: { base: '#3a1a1a', allocated: '#ff4444' },
  mana: { base: '#1a1a3a', allocated: '#4488ff' },
  defense: { base: '#2a2a2a', allocated: '#888888' },
  physical: { base: '#3a2a1a', allocated: '#ff8844' },
  attack_speed: { base: '#3a3a1a', allocated: '#ffcc44' },
  crit: { base: '#3a1a2a', allocated: '#ff44aa' },
  fire: { base: '#3a1a0a', allocated: '#ff4400' },
  lightning: { base: '#2a2a3a', allocated: '#88aaff' },
  cold: { base: '#1a2a3a', allocated: '#44ccff' },
  earth: { base: '#2a2a1a', allocated: '#aa8844' },
  holy: { base: '#3a3a2a', allocated: '#ffff88' },
  evasion: { base: '#1a2a2a', allocated: '#44ffcc' },
  speed: { base: '#2a3a2a', allocated: '#88ff44' },
  leech: { base: '#2a1a1a', allocated: '#cc4466' },
  regen: { base: '#1a3a1a', allocated: '#44ff66' },
  dot: { base: '#2a1a2a', allocated: '#aa44aa' },
  minion: { base: '#1a2a2a', allocated: '#66aacc' },
  mastery: { base: '#2a2a1a', allocated: '#ffaa00' },
  jewel: { base: '#1a2a2a', allocated: '#00ffcc' },
  energy_shield: { base: '#1a1a3a', allocated: '#4488ff' },
  spell: { base: '#2a1a3a', allocated: '#aa66ff' },
};

// Bonus stat Turkish name map for the summary panel
const BONUS_STAT_NAMES: Record<string, string> = {
  flatHp: 'Can',
  flatMana: 'Mana',
  flatArmor: 'Zirh',
  flatEvasion: 'Kacis',
  flatStrength: 'Guc',
  flatAgility: 'Ceviklik',
  flatSpirit: 'Ruh',
  percentHp: '% Can',
  percentMana: '% Mana',
  percentDamage: '% Hasar',
  percentPhysicalDamage: '% Fiziksel Hasar',
  percentElementalDamage: '% Elemental Hasar',
  percentFireDamage: '% Ates Hasari',
  percentLightningDamage: '% Yildirim Hasari',
  percentColdDamage: '% Buz Hasari',
  percentDarkDamage: '% Karanlik Hasari',
  percentAttackSpeed: '% Saldiri Hizi',
  percentMovementSpeed: '% Hareket Hizi',
  percentCritChance: '% Kritik Sansi',
  percentCritDamage: '% Kritik Hasari',
  percentArmor: '% Zirh',
  percentEvasion: '% Kacis',
  percentManaRegen: '% Mana Yenileme',
  percentHpRegen: '% Can Yenileme',
  percentCooldownReduction: '% Bekleme Suresi',
  extraDodgeCharges: 'Ek Kacis Hakki',
  lifeLeechPercent: '% Can Emme',
  manaLeechPercent: '% Mana Emme',
  projectileCount: 'Mermi Sayisi',
  areaOfEffect: 'Alan Etkisi',
  dotDamage: 'DoT Hasari',
  minionDamage: 'Usakci Hasari',
  minionLife: 'Usakci Cani',
  bloodMagic: 'Kan Buyusu',
  mindOverMatter: 'Zihin Kalkani',
};

export class PassiveTreeUI {
  private tree: PassiveTreeManager;
  private isOpen: boolean = false;
  private fadeAlpha: number = 0;
  private screenWidth: number;
  private screenHeight: number;

  // Pan and zoom
  private pan: Vector2 = { x: 0, y: 0 };
  private zoom: number = 1;
  private isDragging: boolean = false;
  private lastMousePos: Vector2 = { x: 0, y: 0 };

  // Hover/selection
  private hoveredNode: string | null = null;
  private tooltipNode: PassiveNode | null = null;

  // Editor mode
  private editMode: boolean = false;
  private draggingNode: string | null = null;
  private nodePositions: Map<string, Vector2> = new Map();

  // Search state
  private searchText: string = '';
  private searchMatches: Set<string> = new Set();
  private searchFocused: boolean = false;
  private cursorBlink: number = 0;

  // Allocation animation effects
  private allocEffects: { nodeId: string; time: number; maxTime: number }[] = [];

  // Mastery selection modal
  private showingMasteryChoice: string | null = null;

  // Callback for when nodes are allocated/deallocated
  private onNodeChange?: () => void;

  constructor(tree: PassiveTreeManager, screenWidth: number, screenHeight: number, onNodeChange?: () => void) {
    this.tree = tree;
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.onNodeChange = onNodeChange;

    // Center view
    this.pan = { x: screenWidth / 2, y: screenHeight / 2 };

    // Load custom node positions from JSON
    const positions = customNodePositions as Record<string, { x: number; y: number }>;
    for (const node of tree.getAllNodes()) {
      if (positions[node.id]) {
        this.nodePositions.set(node.id, { x: positions[node.id].x, y: positions[node.id].y });
      } else {
        // Fallback to tree position if not in custom positions
        this.nodePositions.set(node.id, { x: node.position.x, y: node.position.y });
      }
    }
  }

  update(deltaTime: number): void {
    this.cursorBlink += deltaTime;

    // Update allocation effects
    for (let i = this.allocEffects.length - 1; i >= 0; i--) {
      this.allocEffects[i].time += deltaTime;
      if (this.allocEffects[i].time >= this.allocEffects[i].maxTime) {
        this.allocEffects[i] = this.allocEffects[this.allocEffects.length - 1];
        this.allocEffects.pop();
      }
    }
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) {
      debugLog('PassiveTree', 'Edit mode ON - Drag nodes to reposition. Press E again to exit, X to export JSON');
    } else {
      debugLog('PassiveTree', 'Edit mode OFF');
    }
  }

  isEditModeActive(): boolean {
    return this.editMode;
  }

  exportPositions(): void {
    const positions: Record<string, { x: number; y: number }> = {};
    for (const [id, pos] of this.nodePositions) {
      positions[id] = { x: Math.round(pos.x), y: Math.round(pos.y) };
    }
    const json = JSON.stringify(positions, null, 2);
    debugLog('PassiveTree', '=== NODE POSITIONS JSON ===');
    debugLog('PassiveTree', json);
    debugLog('PassiveTree', '=== END JSON ===');

    // Also copy to clipboard if possible
    if (navigator.clipboard) {
      navigator.clipboard.writeText(json).then(() => {
        debugLog('PassiveTree', 'Positions copied to clipboard!');
      });
    }
  }

  getNodePosition(nodeId: string): Vector2 {
    return this.nodePositions.get(nodeId) || { x: 0, y: 0 };
  }

  setScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) this.fadeAlpha = 0;
  }

  open(): void {
    this.isOpen = true;
    this.fadeAlpha = 0;
  }

  close(): void {
    this.isOpen = false;
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  // Convert tree coordinates to screen coordinates
  private treeToScreen(x: number, y: number): Vector2 {
    return {
      x: this.pan.x + x * this.zoom,
      y: this.pan.y + y * this.zoom,
    };
  }

  // Convert screen coordinates to tree coordinates
  private screenToTree(x: number, y: number): Vector2 {
    return {
      x: (x - this.pan.x) / this.zoom,
      y: (y - this.pan.y) / this.zoom,
    };
  }

  handleKeyDown(key: string): boolean {
    // Close mastery modal on ESC
    if (key === 'Escape' && this.showingMasteryChoice) {
      this.showingMasteryChoice = null;
      return true;
    }

    if (!this.searchFocused) return false;

    if (key === 'Escape') {
      this.searchFocused = false;
      this.searchText = '';
      this.searchMatches.clear();
      return true;
    }

    if (key === 'Backspace') {
      if (this.searchText.length > 0) {
        this.searchText = this.searchText.slice(0, -1);
        this.updateSearchMatches();
      }
      return true;
    }

    // Only accept printable characters (single character keys)
    if (key.length === 1) {
      this.searchText += key;
      this.cursorBlink = 0;
      this.updateSearchMatches();
      return true;
    }

    return false;
  }

  private updateSearchMatches(): void {
    this.searchMatches.clear();
    if (this.searchText.length === 0) return;

    const query = this.searchText.toLowerCase();
    for (const node of this.tree.getAllNodes()) {
      if (node.name.toLowerCase().includes(query)) {
        this.searchMatches.add(node.id);
        continue;
      }
      if (node.description && node.description.toLowerCase().includes(query)) {
        this.searchMatches.add(node.id);
      }
    }
  }

  handleMouseMove(x: number, y: number): void {
    if (!this.isOpen) return;

    const treePos = this.screenToTree(x, y);

    // Editor mode: drag node
    if (this.editMode && this.draggingNode) {
      this.nodePositions.set(this.draggingNode, { x: treePos.x, y: treePos.y });
      this.lastMousePos = { x, y };
      return;
    }

    if (this.isDragging) {
      this.pan.x += x - this.lastMousePos.x;
      this.pan.y += y - this.lastMousePos.y;
    }

    this.lastMousePos = { x, y };

    // Check node hover
    this.hoveredNode = null;
    this.tooltipNode = null;

    for (const node of this.tree.getAllNodes()) {
      const nodePos = this.nodePositions.get(node.id) || node.position;
      const dx = nodePos.x - treePos.x;
      const dy = nodePos.y - treePos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const nodeSize = this.getNodeSize(node.type);
      if (dist < nodeSize) {
        this.hoveredNode = node.id;
        this.tooltipNode = node;
        break;
      }
    }
  }

  handleMouseDown(x: number, y: number): void {
    if (!this.isOpen) return;

    // Check if click is on search bar
    const searchBarX = this.screenWidth - 220;
    const searchBarY = 12;
    const searchBarW = 200;
    const searchBarH = 28;
    if (x >= searchBarX && x <= searchBarX + searchBarW &&
        y >= searchBarY && y <= searchBarY + searchBarH) {
      this.searchFocused = true;
      this.cursorBlink = 0;
      return;
    } else {
      this.searchFocused = false;
    }

    // Editor mode: start dragging node
    if (this.editMode && this.hoveredNode) {
      this.draggingNode = this.hoveredNode;
      return;
    }

    this.isDragging = true;
  }

  handleMouseUp(x: number, y: number): void {
    if (!this.isOpen) return;

    // Editor mode: stop dragging node
    if (this.editMode && this.draggingNode) {
      this.draggingNode = null;
      return;
    }

    this.isDragging = false;

    // Check mastery modal click first
    if (this.showingMasteryChoice) {
      const handled = this.handleMasteryModalClick(x, y);
      if (handled) return;
    }

    // Check if clicking on a node (only in normal mode)
    if (!this.editMode && this.hoveredNode) {
      const node = this.tree.getNode(this.hoveredNode);
      const canAllocate = this.tree.canAllocate(this.hoveredNode);
      const isAllocated = this.tree.getState().allocatedNodes.has(this.hoveredNode);

      // Mastery node: open selection modal
      if (node?.type === 'mastery' && this.tree.isMasteryUnlocked(this.hoveredNode)) {
        this.showingMasteryChoice = this.hoveredNode;
        return;
      }

      if (canAllocate) {
        this.tree.allocateNode(this.hoveredNode);
        this.allocEffects.push({ nodeId: this.hoveredNode, time: 0, maxTime: 0.6 });
        this.onNodeChange?.();
      } else if (isAllocated) {
        this.tree.deallocateNode(this.hoveredNode);
        this.onNodeChange?.();
      }
    }

    // Check close button
    const closeButtonX = this.screenWidth - 40;
    const closeButtonY = 40;
    if (Math.abs(x - closeButtonX) < 20 && Math.abs(y - closeButtonY) < 20) {
      this.close();
    }

    // Check export button (in edit mode)
    if (this.editMode) {
      const exportButtonX = this.screenWidth - 120;
      const exportButtonY = 40;
      if (Math.abs(x - exportButtonX) < 50 && Math.abs(y - exportButtonY) < 20) {
        this.exportPositions();
      }
    }
  }

  handleWheel(delta: number): void {
    if (!this.isOpen) return;

    const oldZoom = this.zoom;
    this.zoom *= delta > 0 ? 0.9 : 1.1;
    this.zoom = Math.max(0.5, Math.min(2, this.zoom));

    // Zoom towards mouse position
    const zoomRatio = this.zoom / oldZoom;
    this.pan.x = this.lastMousePos.x - (this.lastMousePos.x - this.pan.x) * zoomRatio;
    this.pan.y = this.lastMousePos.y - (this.lastMousePos.y - this.pan.y) * zoomRatio;
  }

  private getNodeSize(type: PassiveNodeType): number {
    switch (type) {
      case 'keystone': return 30;
      case 'notable': return 22;
      case 'start': return 25;
      case 'mastery': return 26;
      case 'jewel_socket': return 24;
      case 'gateway': return 12;
      default: return 15;
    }
  }

  private getNodeColor(node: PassiveNode, isAllocated: boolean, isHovered: boolean): string {
    const categoryColor = CATEGORY_COLORS[node.category];

    if (isAllocated) {
      switch (node.type) {
        case 'keystone': return '#ffd700';
        case 'notable': return categoryColor.allocated;
        case 'start': return '#44ff44';
        case 'mastery': return this.tree.isMasteryUnlocked(node.id) ? '#ffaa00' : '#886600';
        case 'jewel_socket': return '#00ffcc';
        case 'gateway': return '#88aaff';
        default: return categoryColor.allocated;
      }
    }

    if (node.type === 'mastery') {
      return this.tree.isMasteryUnlocked(node.id) ? '#665500' : '#332a00';
    }

    if (isHovered && this.tree.canAllocate(node.id)) {
      return '#666688';
    }

    switch (node.type) {
      case 'keystone': return '#4a3a1a';
      case 'notable': return categoryColor.base;
      case 'start': return '#1a4a1a';
      case 'jewel_socket': return '#1a2a2a';
      case 'gateway': return '#1a1a2a';
      default: return categoryColor.base;
    }
  }

  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    // Animate fade-in
    if (this.fadeAlpha < 1) {
      this.fadeAlpha = Math.min(1, this.fadeAlpha + 0.08);
    }

    const ctx = renderer.context;
    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    const state = this.tree.getState();
    const isSearching = this.searchText.length > 0;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Draw class region backgrounds
    this.renderClassRegions(ctx);

    // Draw connections first
    for (const node of this.tree.getAllNodes()) {
      const nodePos = this.nodePositions.get(node.id) || node.position;
      const screenPos = this.treeToScreen(nodePos.x, nodePos.y);

      for (const connId of node.connections) {
        const connNode = this.tree.getNode(connId);
        if (!connNode) continue;

        // Only draw each connection once
        if (connId > node.id) continue;

        const connNodePos = this.nodePositions.get(connId) || connNode.position;
        const connScreenPos = this.treeToScreen(connNodePos.x, connNodePos.y);

        const bothAllocated = state.allocatedNodes.has(node.id) && state.allocatedNodes.has(connId);
        const oneAllocated = state.allocatedNodes.has(node.id) || state.allocatedNodes.has(connId);

        // Fade non-matching connections during search
        if (isSearching) {
          const nodeMatch = this.searchMatches.has(node.id);
          const connMatch = this.searchMatches.has(connId);
          if (!nodeMatch && !connMatch) {
            ctx.globalAlpha = 0.2;
          }
        }

        ctx.beginPath();
        ctx.moveTo(screenPos.x, screenPos.y);
        ctx.lineTo(connScreenPos.x, connScreenPos.y);
        ctx.strokeStyle = bothAllocated ? '#88ccff' : oneAllocated ? '#445566' : '#222233';
        ctx.lineWidth = bothAllocated ? 3 : 2;
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }
    }

    // Draw nodes
    for (const node of this.tree.getAllNodes()) {
      const nodePos = this.nodePositions.get(node.id) || node.position;
      const screenPos = this.treeToScreen(nodePos.x, nodePos.y);
      const isAllocated = state.allocatedNodes.has(node.id);
      const isHovered = this.hoveredNode === node.id;
      const isMatch = this.searchMatches.has(node.id);

      const size = this.getNodeSize(node.type) * this.zoom;
      const color = this.getNodeColor(node, isAllocated, isHovered);

      // Fade non-matching nodes during search
      if (isSearching && !isMatch) {
        ctx.globalAlpha = 0.15;
      }

      // Node glow for keystones
      if (node.type === 'keystone') {
        const gradient = ctx.createRadialGradient(
          screenPos.x, screenPos.y, 0,
          screenPos.x, screenPos.y, size * 1.5
        );
        gradient.addColorStop(0, isAllocated ? 'rgba(255, 215, 0, 0.3)' : 'rgba(100, 80, 50, 0.2)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, size * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      // Search match glow ring (blue) for matching but unallocated nodes
      if (isSearching && isMatch && !isAllocated) {
        ctx.globalAlpha = 1.0;
        const glowGradient = ctx.createRadialGradient(
          screenPos.x, screenPos.y, size * 0.5,
          screenPos.x, screenPos.y, size * 1.8
        );
        glowGradient.addColorStop(0, 'rgba(100, 180, 255, 0.35)');
        glowGradient.addColorStop(1, 'rgba(100, 180, 255, 0)');
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, size * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = glowGradient;
        ctx.fill();

        // Re-apply fade if needed (it was reset for the glow)
        if (isSearching && !isMatch) {
          ctx.globalAlpha = 0.15;
        }
      }

      // Node shape
      ctx.beginPath();
      if (node.type === 'keystone') {
        // Hexagon for keystones
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          const px = screenPos.x + Math.cos(angle) * size;
          const py = screenPos.y + Math.sin(angle) * size;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
      } else if (node.type === 'notable') {
        // Diamond for notables
        ctx.moveTo(screenPos.x, screenPos.y - size);
        ctx.lineTo(screenPos.x + size, screenPos.y);
        ctx.lineTo(screenPos.x, screenPos.y + size);
        ctx.lineTo(screenPos.x - size, screenPos.y);
        ctx.closePath();
      } else if (node.type === 'mastery') {
        // 8-pointed star for mastery
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI / 4) * i - Math.PI / 8;
          const r = i % 2 === 0 ? size : size * 0.55;
          const px = screenPos.x + Math.cos(angle) * r;
          const py = screenPos.y + Math.sin(angle) * r;
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
      } else if (node.type === 'jewel_socket') {
        // Circle with inner ring for jewel sockets
        ctx.arc(screenPos.x, screenPos.y, size, 0, Math.PI * 2);
      } else if (node.type === 'gateway') {
        // Small square for gateways
        const half = size * 0.7;
        ctx.rect(screenPos.x - half, screenPos.y - half, half * 2, half * 2);
      } else {
        // Circle for small and start
        ctx.arc(screenPos.x, screenPos.y, size, 0, Math.PI * 2);
      }

      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = isAllocated ? '#ffffff' : isHovered ? '#888888' : '#444444';
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.stroke();

      // Jewel socket inner circle (hollow center if no jewel inserted)
      if (node.type === 'jewel_socket') {
        const hasJewel = this.tree.getInsertedJewel(node.id) !== null;
        if (!hasJewel) {
          ctx.beginPath();
          ctx.arc(screenPos.x, screenPos.y, size * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = '#0a1515';
          ctx.fill();
          ctx.strokeStyle = isAllocated ? '#00ffcc' : '#334444';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      // Draw category icon inside node
      const icon = CATEGORY_ICONS[node.category];
      if (icon) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Scale font size based on node size
        let fontSize = size * 0.9;
        if (node.type === 'keystone') {
          fontSize = size * 0.7;
        } else if (node.type === 'notable') {
          fontSize = size * 0.75;
        } else if (node.type === 'start') {
          fontSize = size * 0.8;
        }

        ctx.font = `${Math.floor(fontSize)}px Arial`;

        // Add subtle shadow for better visibility
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.fillText(icon, screenPos.x, screenPos.y);
        ctx.restore();
      }

      // Allocatable indicator
      if (!isAllocated && this.tree.canAllocate(node.id)) {
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, size + 5, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(136, 204, 255, 0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Reset alpha after each node
      ctx.globalAlpha = 1.0;
    }

    // Draw minimap
    this.renderMinimap(ctx);

    // Draw allocation effects (expanding rings)
    for (const effect of this.allocEffects) {
      const nodePos = this.nodePositions.get(effect.nodeId);
      if (!nodePos) continue;
      const screenPos = this.treeToScreen(nodePos.x, nodePos.y);
      const t = effect.time / effect.maxTime;
      const radius = 20 + t * 60;
      const alpha = 1 - t;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, radius * this.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 215, 0, ${alpha * 0.8})`;
      ctx.lineWidth = 3 * (1 - t);
      ctx.stroke();
      // Inner flash
      if (t < 0.3) {
        const flashAlpha = (1 - t / 0.3) * 0.4;
        const gradient = ctx.createRadialGradient(screenPos.x, screenPos.y, 0, screenPos.x, screenPos.y, 30 * this.zoom);
        gradient.addColorStop(0, `rgba(255, 255, 200, ${flashAlpha})`);
        gradient.addColorStop(1, 'rgba(255, 255, 200, 0)');
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 30 * this.zoom, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    // Draw tooltip
    if (this.tooltipNode) {
      this.renderTooltip(ctx);
    }

    // Draw mastery selection modal
    if (this.showingMasteryChoice) {
      this.renderMasteryModal(ctx);
    }

    // UI elements
    this.renderUI(renderer);

    ctx.restore(); // restore globalAlpha from fade-in
  }

  private renderClassRegions(ctx: CanvasRenderingContext2D): void {
    const centerScreen = this.treeToScreen(0, 0);
    const regionRadius = 1200 * this.zoom;

    // Class regions: angle in radians, converted from degrees
    const regions = [
      { name: 'ALP', angleDeg: 180, startDeg: 140, endDeg: 220, color: 'rgba(255, 50, 50, 0.03)', labelColor: 'rgba(255,100,100,0.5)' },
      { name: 'KAM', angleDeg: 300, startDeg: 260, endDeg: 340, color: 'rgba(100, 50, 255, 0.03)', labelColor: 'rgba(150,100,255,0.5)' },
      { name: 'MERGEN', angleDeg: 60, startDeg: 20, endDeg: 100, color: 'rgba(50, 255, 100, 0.03)', labelColor: 'rgba(100,255,150,0.5)' },
    ];

    for (const region of regions) {
      const startRad = (region.startDeg * Math.PI) / 180;
      const endRad = (region.endDeg * Math.PI) / 180;

      // Draw filled arc wedge
      ctx.beginPath();
      ctx.moveTo(centerScreen.x, centerScreen.y);
      ctx.arc(centerScreen.x, centerScreen.y, regionRadius, startRad, endRad);
      ctx.closePath();
      ctx.fillStyle = region.color;
      ctx.fill();

      // Draw class label at outer edge
      const labelRadius = 1100 * this.zoom;
      const labelAngleRad = (region.angleDeg * Math.PI) / 180;
      const labelX = centerScreen.x + Math.cos(labelAngleRad) * labelRadius;
      const labelY = centerScreen.y + Math.sin(labelAngleRad) * labelRadius;

      ctx.save();
      ctx.font = `bold ${Math.floor(18 * this.zoom)}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = region.labelColor;
      ctx.fillText(region.name, labelX, labelY);
      ctx.restore();
    }
  }

  private renderMinimap(ctx: CanvasRenderingContext2D): void {
    const mapSize = 150;
    const mapX = 20;
    const mapY = this.screenHeight - 170;
    const padding = 5;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(mapX, mapY, mapSize, mapSize);
    ctx.strokeStyle = '#444466';
    ctx.lineWidth = 1;
    ctx.strokeRect(mapX, mapY, mapSize, mapSize);

    // Calculate bounding box of all node positions
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const allNodes = this.tree.getAllNodes();

    for (const node of allNodes) {
      const pos = this.nodePositions.get(node.id) || node.position;
      if (pos.x < minX) minX = pos.x;
      if (pos.y < minY) minY = pos.y;
      if (pos.x > maxX) maxX = pos.x;
      if (pos.y > maxY) maxY = pos.y;
    }

    // Add margin
    const rangeX = (maxX - minX) || 1;
    const rangeY = (maxY - minY) || 1;
    const scale = Math.min(
      (mapSize - padding * 2) / rangeX,
      (mapSize - padding * 2) / rangeY
    );
    const offsetX = mapX + padding + ((mapSize - padding * 2) - rangeX * scale) / 2;
    const offsetY = mapY + padding + ((mapSize - padding * 2) - rangeY * scale) / 2;

    const state = this.tree.getState();

    // Draw nodes as tiny dots
    for (const node of allNodes) {
      const pos = this.nodePositions.get(node.id) || node.position;
      const dotX = offsetX + (pos.x - minX) * scale;
      const dotY = offsetY + (pos.y - minY) * scale;
      const isAllocated = state.allocatedNodes.has(node.id);

      const dotSize = node.type === 'keystone' ? 3 : node.type === 'notable' ? 2 : node.type === 'mastery' ? 2.5 : node.type === 'jewel_socket' ? 2 : node.type === 'gateway' ? 1 : 1;

      ctx.beginPath();
      ctx.arc(dotX, dotY, dotSize, 0, Math.PI * 2);
      ctx.fillStyle = isAllocated ? '#d4af37' : '#333333';
      ctx.fill();
    }

    // Draw viewport rectangle showing current visible area
    const topLeft = this.screenToTree(0, 0);
    const bottomRight = this.screenToTree(this.screenWidth, this.screenHeight);

    const vpLeft = offsetX + (topLeft.x - minX) * scale;
    const vpTop = offsetY + (topLeft.y - minY) * scale;
    const vpRight = offsetX + (bottomRight.x - minX) * scale;
    const vpBottom = offsetY + (bottomRight.y - minY) * scale;

    // Clamp to minimap bounds
    const clampedLeft = Math.max(mapX, Math.min(mapX + mapSize, vpLeft));
    const clampedTop = Math.max(mapY, Math.min(mapY + mapSize, vpTop));
    const clampedRight = Math.max(mapX, Math.min(mapX + mapSize, vpRight));
    const clampedBottom = Math.max(mapY, Math.min(mapY + mapSize, vpBottom));

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.strokeRect(clampedLeft, clampedTop, clampedRight - clampedLeft, clampedBottom - clampedTop);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(clampedLeft, clampedTop, clampedRight - clampedLeft, clampedBottom - clampedTop);
  }

  private renderSearchBar(ctx: CanvasRenderingContext2D): void {
    const barW = 200;
    const barH = 28;
    const barX = this.screenWidth - 220;
    const barY = 12;

    // Background
    ctx.fillStyle = this.searchFocused ? '#1a1815' : '#141210';
    ctx.fillRect(barX, barY, barW, barH);

    // Border
    ctx.strokeStyle = this.searchFocused ? '#d4af37' : '#666666';
    ctx.lineWidth = 1;
    ctx.strokeRect(barX, barY, barW, barH);

    // Placeholder or typed text
    ctx.font = '13px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    if (this.searchText.length === 0 && !this.searchFocused) {
      ctx.fillStyle = '#606050';
      ctx.fillText('Ara...', barX + 8, barY + barH / 2);
    } else {
      ctx.fillStyle = '#d0c8b0';
      // Clip text to bar width
      ctx.save();
      ctx.beginPath();
      ctx.rect(barX + 4, barY, barW - 8, barH);
      ctx.clip();
      ctx.fillText(this.searchText, barX + 8, barY + barH / 2);

      // Blinking cursor
      if (this.searchFocused && Math.sin(this.cursorBlink * 4) > 0) {
        const textW = ctx.measureText(this.searchText).width;
        ctx.fillStyle = '#d4af37';
        ctx.fillRect(barX + 8 + textW + 1, barY + 6, 1, barH - 12);
      }
      ctx.restore();
    }

    ctx.textBaseline = 'alphabetic';
  }

  private renderBonusSummary(ctx: CanvasRenderingContext2D): void {
    const panelX = this.screenWidth - 200;
    const panelY = 100;
    const panelW = 180;
    const lineHeight = 18;
    const padding = 10;

    const bonuses = this.tree.getTotalBonuses();
    const entries: { label: string; value: string }[] = [];

    for (const [key, val] of Object.entries(bonuses)) {
      if (val === undefined || val === null) continue;
      if (typeof val === 'number' && val === 0) continue;
      if (typeof val === 'boolean' && !val) continue;

      const name = BONUS_STAT_NAMES[key] || key;

      if (typeof val === 'boolean') {
        entries.push({ label: name, value: t('skillsV2.status.active') });
      } else {
        const sign = val >= 0 ? '+' : '';
        entries.push({ label: name, value: `${sign}${val}` });
      }
    }

    if (entries.length === 0) return;

    const maxVisible = 12;
    const visibleEntries = entries.slice(0, maxVisible);
    const hasMore = entries.length > maxVisible;

    const panelH = padding * 2 + 22 + visibleEntries.length * lineHeight + (hasMore ? lineHeight : 0);

    // Background
    ctx.fillStyle = 'rgba(20, 20, 30, 0.8)';
    ctx.fillRect(panelX, panelY, panelW, panelH);
    ctx.strokeStyle = '#444466';
    ctx.lineWidth = 1;
    ctx.strokeRect(panelX, panelY, panelW, panelH);

    // Title
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Toplam Bonuslar', panelX + padding, panelY + padding + 12);

    // Bonus entries
    ctx.font = '12px Arial';
    let textY = panelY + padding + 32;
    for (const entry of visibleEntries) {
      ctx.fillStyle = '#88ff88';
      ctx.fillText(`${entry.value} ${entry.label}`, panelX + padding, textY);
      textY += lineHeight;
    }

    // Show "..." if more
    if (hasMore) {
      ctx.fillStyle = '#888888';
      ctx.fillText('...', panelX + padding, textY);
    }
  }

  private renderTooltip(ctx: CanvasRenderingContext2D): void {
    if (!this.tooltipNode) return;

    const node = this.tooltipNode;
    const x = this.lastMousePos.x + 20;
    const y = this.lastMousePos.y + 20;

    const padding = 10;
    const lineHeight = 18;
    const titleHeight = 24;

    // Get category icon
    const categoryIcon = CATEGORY_ICONS[node.category];
    const titleWithIcon = `${categoryIcon} ${node.name}`;

    // Calculate tooltip size - handle multiline descriptions
    ctx.font = '14px Arial';
    const descLines = node.description.split('\n');
    let maxDescWidth = 0;
    for (const line of descLines) {
      const lineWidth = ctx.measureText(line).width;
      if (lineWidth > maxDescWidth) maxDescWidth = lineWidth;
    }
    // Count extra lines for special node types
    let extraLineCount = 0;
    if (node.type === 'mastery' || node.type === 'jewel_socket' || node.type === 'gateway') {
      extraLineCount = 1;
    }

    const nameWidth = ctx.measureText(titleWithIcon).width;
    const width = Math.max(maxDescWidth, nameWidth, 200) + padding * 2 + 20;
    const height = titleHeight + ((descLines.length + extraLineCount) * lineHeight) + padding * 2 + 16;

    // Adjust position if near edge
    let tooltipX = x;
    let tooltipY = y;
    if (tooltipX + width > this.screenWidth) tooltipX = x - width - 40;
    if (tooltipY + height > this.screenHeight) tooltipY = y - height - 40;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 30, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, width, height);

    // Border
    ctx.strokeStyle = this.getTooltipBorderColor(node.type, node.category);
    ctx.lineWidth = 2;
    ctx.strokeRect(tooltipX, tooltipY, width, height);

    // Name with icon
    ctx.fillStyle = this.getTooltipTitleColor(node.type);
    ctx.font = 'bold 14px Arial';
    ctx.fillText(titleWithIcon, tooltipX + padding, tooltipY + padding + 14);

    // Description (multiline support)
    ctx.fillStyle = '#cccccc';
    ctx.font = '12px Arial';
    for (let i = 0; i < descLines.length; i++) {
      ctx.fillText(descLines[i], tooltipX + padding, tooltipY + padding + titleHeight + 12 + (i * lineHeight));
    }

    // Extra info for special node types
    let extraLines: string[] = [];
    if (node.type === 'mastery' && node.masteryData) {
      const unlocked = this.tree.isMasteryUnlocked(node.id);
      if (unlocked) {
        const selected = this.tree.getSelectedMastery(node.id);
        if (selected) {
          extraLines.push(`Secim: ${selected.name}`);
        } else {
          extraLines.push('Secim yapmak icin tikla');
        }
      } else {
        extraLines.push('Kilit: gerekli notable\'lari al');
      }
    } else if (node.type === 'jewel_socket') {
      const jewel = this.tree.getInsertedJewel(node.id);
      if (jewel) {
        extraLines.push(`Mucevher: ${jewel.name}`);
      } else {
        extraLines.push('Bos yuva');
      }
    } else if (node.type === 'gateway') {
      extraLines.push('Gecis noktasi (bedava)');
    }

    for (let i = 0; i < extraLines.length; i++) {
      ctx.fillStyle = '#ffcc66';
      ctx.fillText(extraLines[i], tooltipX + padding, tooltipY + padding + titleHeight + 12 + ((descLines.length + i) * lineHeight));
    }

    // Status
    const state = this.tree.getState();
    const isAllocated = state.allocatedNodes.has(node.id);
    const canAllocate = this.tree.canAllocate(node.id);

    let statusText = '';
    let statusColor = '#888888';

    if (node.type === 'mastery') {
      if (this.tree.isMasteryUnlocked(node.id)) {
        statusText = 'Acik - secim yapmak icin tikla';
        statusColor = '#ffaa00';
      } else {
        statusText = 'Kilitli';
        statusColor = '#aa4444';
      }
    } else if (isAllocated) {
      statusText = 'Tahsis Edildi (kaldirmak icin tikla)';
      statusColor = '#88ff88';
    } else if (canAllocate) {
      statusText = 'Tahsis etmek icin tikla';
      statusColor = '#88ccff';
    } else {
      statusText = 'Bagli bir dugum tahsis edin';
      statusColor = '#aa4444';
    }

    ctx.fillStyle = statusColor;
    ctx.font = '10px Arial';
    const statusY = tooltipY + padding + titleHeight + ((descLines.length + extraLines.length) * lineHeight) + 8;
    ctx.fillText(statusText, tooltipX + padding, statusY);
  }

  private getTooltipBorderColor(type: PassiveNodeType, category?: NodeCategory): string {
    if (type === 'keystone') return '#ffd700';
    if (type === 'start') return '#44ff44';
    if (type === 'mastery') return '#ffaa00';
    if (type === 'jewel_socket') return '#00ffcc';
    if (type === 'gateway') return '#88aaff';
    if (category && CATEGORY_COLORS[category]) {
      return CATEGORY_COLORS[category].allocated;
    }
    switch (type) {
      case 'notable': return '#44aaff';
      default: return '#666666';
    }
  }

  private getTooltipTitleColor(type: PassiveNodeType): string {
    switch (type) {
      case 'keystone': return '#ffd700';
      case 'notable': return '#88ccff';
      case 'start': return '#88ff88';
      case 'mastery': return '#ffaa00';
      case 'jewel_socket': return '#00ffcc';
      case 'gateway': return '#88aaff';
      default: return '#ffffff';
    }
  }

  private renderUI(renderer: Renderer): void {
    const ctx = renderer.context;
    const state = this.tree.getState();

    // Title
    ctx.fillStyle = this.editMode ? '#ff8800' : '#ffd700';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    const title = this.editMode ? 'EDIT MODE - Pasif Yetenek Agaci' : 'Pasif Yetenek Agaci';
    ctx.fillText(title, this.screenWidth / 2, 40);

    // Available points / Edit mode info
    ctx.fillStyle = '#ffffff';
    ctx.font = '16px Arial';
    if (this.editMode) {
      ctx.fillText('Node\'lari surukleyip birakin | E: Cikis | X: JSON Export', this.screenWidth / 2, 70);
    } else {
      ctx.fillText(`Kullanilabilir Puan: ${state.availablePoints}`, this.screenWidth / 2, 70);
    }

    // Close button
    ctx.fillStyle = '#aa4444';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'right';
    ctx.fillText('X', this.screenWidth - 30, 45);

    // Export button (in edit mode)
    if (this.editMode) {
      ctx.fillStyle = '#44aa44';
      ctx.fillRect(this.screenWidth - 170, 25, 100, 30);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('EXPORT', this.screenWidth - 120, 45);
    }

    // Category legend (left side)
    this.renderLegend(ctx);

    // Search bar (top-right)
    this.renderSearchBar(ctx);

    // Bonus summary panel (right side)
    this.renderBonusSummary(ctx);

    // Instructions
    ctx.fillStyle = '#888888';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Kaydirmak icin surukle | Yakinlastirmak icin scroll | ESC kapatmak icin', this.screenWidth / 2, this.screenHeight - 20);

    ctx.textAlign = 'left';
  }

  private renderLegend(ctx: CanvasRenderingContext2D): void {
    const legendX = 20;
    let legendY = 110;
    const lineHeight = 22;

    // Legend background
    ctx.fillStyle = 'rgba(20, 20, 30, 0.8)';
    ctx.fillRect(legendX - 5, legendY - 20, 130, 290);
    ctx.strokeStyle = '#444466';
    ctx.lineWidth = 1;
    ctx.strokeRect(legendX - 5, legendY - 20, 130, 290);

    // Legend title
    ctx.fillStyle = '#aaaaaa';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Kategoriler', legendX, legendY - 5);
    legendY += 15;

    // Show key categories with icons
    const keyCategories: { key: NodeCategory; label: string }[] = [
      { key: 'strength', label: 'Guc' },
      { key: 'agility', label: 'Ceviklik' },
      { key: 'spirit', label: 'Ruh' },
      { key: 'life', label: 'Can' },
      { key: 'mana', label: 'Mana' },
      { key: 'defense', label: 'Savunma' },
      { key: 'crit', label: 'Kritik' },
      { key: 'fire', label: 'Ates' },
      { key: 'lightning', label: 'Simsek' },
      { key: 'cold', label: 'Buz' },
      { key: 'leech', label: 'Calma' },
      { key: 'regen', label: 'Yenilenme' },
      { key: 'dot', label: 'DoT' },
      { key: 'minion', label: 'Minyon' },
    ];

    ctx.font = '12px Arial';
    for (const { key, label } of keyCategories) {
      const icon = CATEGORY_ICONS[key];
      ctx.fillStyle = CATEGORY_COLORS[key].allocated;
      ctx.fillText(`${icon} ${label}`, legendX, legendY);
      legendY += lineHeight - 4;
    }
  }

  // ========================
  // Mastery Selection Modal
  // ========================

  private renderMasteryModal(ctx: CanvasRenderingContext2D): void {
    const masteryId = this.showingMasteryChoice;
    if (!masteryId) return;
    const node = this.tree.getNode(masteryId);
    if (!node?.masteryData) { this.showingMasteryChoice = null; return; }

    const options = node.masteryData.options;
    const modalW = 320;
    const optionH = 50;
    const padding = 16;
    const titleH = 36;
    const modalH = titleH + options.length * optionH + padding * 2;
    const modalX = (this.screenWidth - modalW) / 2;
    const modalY = (this.screenHeight - modalH) / 2;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Modal background
    ctx.fillStyle = 'rgba(20, 18, 14, 0.98)';
    ctx.fillRect(modalX, modalY, modalW, modalH);
    ctx.strokeStyle = '#ffaa00';
    ctx.lineWidth = 2;
    ctx.strokeRect(modalX, modalY, modalW, modalH);

    // Title
    ctx.fillStyle = '#ffaa00';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(node.name, modalX + modalW / 2, modalY + padding + 16);

    // Options
    const selectedId = this.tree.getState().masterySelections.get(masteryId);
    ctx.textAlign = 'left';
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const optY = modalY + titleH + i * optionH + padding;
      const isSelected = opt.id === selectedId;

      // Option background
      ctx.fillStyle = isSelected ? 'rgba(255, 170, 0, 0.15)' : 'rgba(40, 36, 28, 0.8)';
      ctx.fillRect(modalX + 8, optY, modalW - 16, optionH - 4);
      ctx.strokeStyle = isSelected ? '#ffaa00' : '#554422';
      ctx.lineWidth = 1;
      ctx.strokeRect(modalX + 8, optY, modalW - 16, optionH - 4);

      // Icon + Name
      const icon = opt.icon || '🌟';
      ctx.fillStyle = isSelected ? '#ffcc44' : '#ddcc88';
      ctx.font = 'bold 13px Arial';
      ctx.fillText(`${icon} ${opt.name}`, modalX + 16, optY + 18);

      // Description
      ctx.fillStyle = '#aaa';
      ctx.font = '11px Arial';
      ctx.fillText(opt.description, modalX + 16, optY + 36);
    }

    // Close hint
    ctx.fillStyle = '#666';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Tikla: sec | ESC: kapat', modalX + modalW / 2, modalY + modalH - 6);
    ctx.textAlign = 'left';
  }

  private handleMasteryModalClick(x: number, y: number): boolean {
    const masteryId = this.showingMasteryChoice;
    if (!masteryId) return false;
    const node = this.tree.getNode(masteryId);
    if (!node?.masteryData) { this.showingMasteryChoice = null; return true; }

    const options = node.masteryData.options;
    const modalW = 320;
    const optionH = 50;
    const padding = 16;
    const titleH = 36;
    const modalH = titleH + options.length * optionH + padding * 2;
    const modalX = (this.screenWidth - modalW) / 2;
    const modalY = (this.screenHeight - modalH) / 2;

    // Check if click is on an option
    for (let i = 0; i < options.length; i++) {
      const optY = modalY + titleH + i * optionH + padding;
      if (x >= modalX + 8 && x <= modalX + modalW - 8 &&
          y >= optY && y <= optY + optionH - 4) {
        this.tree.selectMasteryOption(masteryId, options[i].id);
        this.showingMasteryChoice = null;
        this.onNodeChange?.();
        return true;
      }
    }

    // Click outside modal closes it
    if (x < modalX || x > modalX + modalW || y < modalY || y > modalY + modalH) {
      this.showingMasteryChoice = null;
      return true;
    }

    return true;
  }
}
